/* Collection grid: facets, sort and pagination through the Section Rendering API, the mobile filter sheet,
   applied-filter chips. Every state is a URL (history.pushState), so back/forward and sharing work. */
import { $, $$, layers, debounce, onReady, scrollMode } from '@theme/utils';

function init(root) {
  if (root.__coll) return; root.__coll = true;
  const sectionId = root.dataset.sectionId, baseUrl = root.dataset.url || location.pathname;
  // the alternate template this grid was opened in (?view=all from a hub): every request keeps it, or the
  // section would be looked up in the hub template, which has no grid
  const view = root.dataset.view || '';
  const facets = () => root.querySelector('[data-layer="filters"]');
  let controller = null, seq = 0;

  function withView(url) {
    if (!view) return url;
    const u = new URL(url, location.origin);
    if (!u.searchParams.has('view')) u.searchParams.set('view', view);
    return u.pathname + u.search + u.hash;
  }

  // the section through the Section Rendering API, cancelled when a newer request starts
  async function fetchGrid(url, signal) {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { headers: { Accept: 'text/html' }, signal });
    if (!res.ok) throw new Error('Section ' + sectionId + ' failed');
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return doc.querySelector('#shopify-section-' + sectionId) || doc.body;
  }

  function paramsFromForm() {
    const form = root.querySelector('form[data-filters]');
    const p = new URLSearchParams();
    if (form) new FormData(form).forEach((v, k) => { if (String(v).trim() !== '') p.append(k, v); });
    const sort = root.querySelector('[data-sort]');
    if (sort && sort.value && !p.has('sort_by')) p.set('sort_by', sort.value);
    return p;
  }

  async function load(url, { push = true, scroll = false } = {}) {
    url = withView(url);
    const grid = root.querySelector('[data-coll-grid]');
    if (grid) grid.classList.add('is-loading');
    if (controller) controller.abort(); controller = new AbortController();
    // only the latest request may touch the page: an older answer that arrives late is dropped
    const mine = ++seq;
    try {
      const fresh = await fetchGrid(url, controller.signal);
      if (mine !== seq) return;
      const next = fresh.querySelector('[data-collection-root]') || fresh;
      ['[data-coll-results]', '[data-applied]', '[data-count]'].forEach((sel) => {
        const a = root.querySelector(sel), b = next.querySelector(sel); if (a && b) a.replaceWith(b);
      });
      // facets: keep the open panel and focus, refresh counts/availability, hidden sort input and the Show button
      const fa = facets(), fb = next.querySelector('[data-layer="filters"]');
      if (fa && fb) {
        $$('input[type=checkbox]', fb).forEach((nb) => {
          const ob = fa.querySelector(`input[name="${CSS.escape(nb.name)}"][value="${CSS.escape(nb.value)}"]`);
          if (!ob) return;
          ob.disabled = nb.disabled; ob.checked = nb.checked;
          const on = ob.parentElement.querySelector('.n'), nn = nb.parentElement.querySelector('.n'); if (on && nn) on.textContent = nn.textContent;
        });
        const sb = fb.querySelector('.msort button'), sa = fa.querySelector('.msort button'); if (sa && sb) sa.textContent = sb.textContent;
        const hidA = fa.querySelector('input[name="sort_by"]'), hidB = fb.querySelector('input[name="sort_by"]');
        if (hidA && !hidB) hidA.remove(); else if (!hidA && hidB) fa.querySelector('form').prepend(hidB); else if (hidA && hidB) hidA.value = hidB.value;
      }
      const ob = root.querySelector('[data-filters-open]'), nb = next.querySelector('[data-filters-open]'); if (ob && nb) ob.textContent = nb.textContent;
      if (push) history.pushState({ nocryCollection: true }, '', url);
      if (scroll) root.scrollIntoView({ block: 'start', behavior: scrollMode() });
      document.dispatchEvent(new CustomEvent('nocry:section:rendered', { detail: { root } }));
    } catch (e) {
      if (e.name !== 'AbortError' && mine === seq) location.href = url;
    } finally { if (mine === seq && grid) grid.classList.remove('is-loading'); }
  }

  const apply = () => { const p = paramsFromForm(); load(baseUrl + (p.toString() ? '?' + p.toString() : '')); };
  const applyDebounced = debounce(apply, 400);

  root.addEventListener('change', (e) => {
    if (e.target.matches('[data-sort]')) {
      $$('[data-sort]', root).forEach((s) => { if (s !== e.target) s.value = e.target.value; });
      const form = root.querySelector('form[data-filters]');
      if (form) { let h = form.querySelector('input[name="sort_by"]'); if (!h) { h = document.createElement('input'); h.type = 'hidden'; h.name = 'sort_by'; form.prepend(h); } h.value = e.target.value; }
      apply(); return;
    }
    if (e.target.closest('form[data-filters]')) { if (e.target.type === 'number') applyDebounced(); else apply(); }
  });
  root.addEventListener('submit', (e) => { if (e.target.matches('form[data-filters]')) { e.preventDefault(); apply(); } });
  root.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-filter-remove]');
    if (rm) { e.preventDefault(); load(rm.getAttribute('href')); return; }
    const pg = e.target.closest('[data-pagination] a');
    if (pg) { e.preventDefault(); load(pg.getAttribute('href'), { scroll: true }); return; }
    if (e.target.closest('[data-filters-open]')) { e.preventDefault(); layers.open('filters', facets()); e.target.closest('[data-filters-open]').setAttribute('aria-expanded', 'true'); return; }
    if (e.target.closest('[data-filters-close]')) { e.preventDefault(); layers.close('filters'); const b = root.querySelector('[data-filters-open]'); if (b) b.setAttribute('aria-expanded', 'false'); }
  });
  document.addEventListener('nocry:layer:close', (e) => { if (e.detail.name === 'filters') { const b = root.querySelector('[data-filters-open]'); if (b) b.setAttribute('aria-expanded', 'false'); } });
  addEventListener('popstate', () => load(location.href, { push: false }));
}

onReady(() => $$('[data-collection-root]').forEach(init));
document.addEventListener('shopify:section:load', (e) => $$('[data-collection-root]', e.target).forEach(init));
